import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Image, Palette, Plus, Trash2, Settings, HelpCircle } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { useOrgaConfigurationContext, useAuth } from '@/app/providers';
import { usePermissionsHook } from '@/shared/hooks/usePermissionsHook';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { StorageService } from '@/services/firebase/storage-service';
import {
  getOrganizationLogoErrorMessage,
  getOrganizationLogoUrl,
  ORGANIZATION_LOGO_ACCEPT,
  validateOrganizationLogoFile,
} from '@/shared/utils/organization-logo';
import { Card, CardHeader, CardBody, Button } from '@/components/ui';

interface PersonalizationConfigurationFormData {
  personalization?: {
    logo?: string;
    description?: string;
    informativeAnnouncement?: string;
    productModelConfig?: {
      format: 'structured' | 'free' | 'custom';
      modelPlaceholder?: string;
      typePlaceholder?: string;
      sizePlaceholder?: string;
      customFields?: Array<{
        key: string;
        label: string;
        placeholder?: string;
        type: 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'url' | 'date';
        required: boolean;
      }>;
    };
  };
}

const PersonalizationConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const { idorga } = useParams<{ idorga: string }>();
  const navigate = useNavigate();
  const { configuration } = useOrgaConfigurationContext();
  const { currentUser } = useAuth();
  const { canManageConfiguration } = usePermissionsHook();

  // Vérifier si l'utilisateur a les droits pour gérer la personnalisation
  const canManagePersonalization = idorga && currentUser
    ? canManageConfiguration(idorga)
    : false;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const form = useForm<PersonalizationConfigurationFormData>({
    defaultValues: {
      personalization: {
        logo: '',
        description: '',
        informativeAnnouncement: '',
        productModelConfig: {
          format: 'custom',
          modelPlaceholder: '',
          typePlaceholder: '',
          sizePlaceholder: '',
          customFields: [], // Par défaut, aucun champ - l'utilisateur les ajoute manuellement
        },
      },
    },
  });

  // Update form when configuration loads
  useEffect(() => {
    if (configuration) {
      form.reset({
        personalization: {
          logo: configuration.personalization?.logo || '',
          description: configuration.personalization?.description || '',
          informativeAnnouncement: configuration.personalization?.informativeAnnouncement || '',
          productModelConfig: {
            format: configuration.personalization?.productModelConfig?.format || 'custom',
            modelPlaceholder: configuration.personalization?.productModelConfig?.modelPlaceholder || '',
            typePlaceholder: configuration.personalization?.productModelConfig?.typePlaceholder || '',
            sizePlaceholder: configuration.personalization?.productModelConfig?.sizePlaceholder || '',
            // Utiliser les champs configurés ou un tableau vide si rien n'est configuré
            customFields: configuration.personalization?.productModelConfig?.customFields || [],
          },
        },
      });
      setLogoPreview(getOrganizationLogoUrl(configuration) || null);
    }
  }, [configuration, form]);

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        validateOrganizationLogoFile(file);
      } catch (error) {
        toast.error(getOrganizationLogoErrorMessage(error, t));
        event.target.value = '';
        return;
      }

      setLogoFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (data: PersonalizationConfigurationFormData) => {
    if (!idorga) {
      toast.error(t('admin.configuration.general.missingOrgId'));
      return;
    }

    setIsSaving(true);
    try {
      let logoUrl = configuration?.personalization?.logo; // Conserver l'URL existante par défaut

      // Si un nouveau logo a été sélectionné, le télécharger
      if (logoFile) {
        console.log('📤 Téléchargement du nouveau logo...');
        logoUrl = await StorageService.uploadOrganizationLogo(logoFile, idorga);
        console.log('✅ Logo téléchargé:', logoUrl);
      }

      const updateData = {
        personalization: {
          ...data.personalization,
          logo: logoUrl,
        },
      };

      await OrgaConfigurationRepository.update(idorga, updateData);
      toast.success(t('admin.configuration.general.updateSuccess'));
      setIsEditing(false);
      setLogoFile(null); // Réinitialiser le fichier sélectionné
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(
        logoFile
          ? getOrganizationLogoErrorMessage(error, t)
          : t('admin.configuration.general.updateError'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (configuration) {
      form.reset({
        personalization: {
          logo: configuration.personalization?.logo || '',
          description: configuration.personalization?.description || '',
          informativeAnnouncement: configuration.personalization?.informativeAnnouncement || '',
          productModelConfig: {
            format: configuration.personalization?.productModelConfig?.format || 'custom',
            modelPlaceholder: configuration.personalization?.productModelConfig?.modelPlaceholder || '',
            typePlaceholder: configuration.personalization?.productModelConfig?.typePlaceholder || '',
            sizePlaceholder: configuration.personalization?.productModelConfig?.sizePlaceholder || '',
            // Utiliser les champs configurés ou un tableau vide si rien n'est configuré
            customFields: configuration.personalization?.productModelConfig?.customFields || [],
          },
        },
      });
      setLogoPreview(getOrganizationLogoUrl(configuration) || null);
    }
    setIsEditing(false);
    setLogoFile(null); // Réinitialiser le fichier sélectionné
  };

  return (
    <div className="space-y-6 min-h-[600px]">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground leading-tight">{t('admin.configuration.sections.personalization.title')}</h2>
          <p className="text-xs sm:text-sm text-foreground-secondary mt-0.5 sm:mt-1">{t('admin.configuration.sections.personalization.description')}</p>
        </div>
        {!isEditing && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsEditing(true)}
            leftIcon={<Settings className="w-4 h-4" />}
            className="w-full sm:w-auto self-start sm:self-auto"
          >
            {t('admin.configuration.general.modify')}
          </Button>
        )}
      </div>

      <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">
        {/* Personnalisation visuelle */}
        <Card variant="default">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-info-10 flex items-center justify-center">
                <Palette className="w-5 h-5 text-info" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Personnalisation visuelle</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">La personnalisation est maintenant disponible directement dans le formulaire de commande pour un aperçu en temps réel</p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="text-center py-8">
              <p className="text-sm text-foreground-secondary mb-4">
                Utilisez le bouton "Personnaliser l'apparence" dans le header pour accéder à l'éditeur de personnalisation directement dans le formulaire de commande.
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  if (!canManagePersonalization) {
                    toast.error(t('admin.configuration.personalization.noPermission') || 'Vous n\'avez pas les droits pour accéder à la personnalisation');
                    return;
                  }
                  if (idorga) {
                    navigate(`/orga/${idorga}/order?edit=personalization`);
                  }
                }}
                disabled={!canManagePersonalization}
                leftIcon={<Palette className="w-4 h-4" />}
              >
                Ouvrir l'éditeur de personnalisation
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Logo */}
        <Card variant="default">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary-10 flex items-center justify-center">
                <Image className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('admin.configuration.general.logo')}</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">{t('admin.configuration.general.logoFormats')}</p>
                <p className="text-xs text-foreground-secondary mt-1">{t('admin.configuration.general.logoSharedFieldHint')}</p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="w-20 h-20 border-2 border-border rounded-lg overflow-hidden bg-background-tertiary flex-shrink-0">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground-secondary">
                    <Image className="w-8 h-8" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  id="logo-upload"
                  type="file"
                  accept={ORGANIZATION_LOGO_ACCEPT}
                  onChange={handleLogoChange}
                  disabled={!isEditing}
                  className="block w-full text-sm text-foreground-secondary file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-10 file:text-primary hover:file:bg-primary-20 disabled:opacity-50"
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Informations de base */}
        <Card variant="default">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-info-10 flex items-center justify-center">
                <Settings className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('admin.configuration.general.basicInfo')}</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">{t('admin.configuration.general.basicInfoDescription')}</p>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
                {t('admin.configuration.general.description')}
              </label>
              <RichTextEditor
                value={form.watch('personalization.description') || ''}
                onChange={(value) => form.setValue('personalization.description', value, { shouldValidate: true })}
                placeholder={t('admin.configuration.general.descriptionPlaceholder')}
                rows={3}
                disabled={!isEditing}
                theme={{
                  colors: {
                    border: 'var(--border)',
                    background: 'var(--surface)',
                    backgroundTertiary: 'var(--background-tertiary)',
                    foreground: 'var(--foreground)',
                    foregroundSecondary: 'var(--foreground-secondary)'
                  }
                }}
              />
              {form.formState.errors.personalization?.description && (
                <p className="mt-1 text-xs text-error">{form.formState.errors.personalization.description.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <label htmlFor="informativeAnnouncement" className="block text-sm font-medium text-foreground">
                  {t('admin.configuration.general.informativeAnnouncement')}
                </label>
                <Tooltip
                  title={t('admin.configuration.general.informativeAnnouncementTooltip')}
                  arrow
                  placement="top"
                >
                  <HelpCircle className="w-4 h-4 text-foreground-secondary cursor-help" />
                </Tooltip>
              </div>
              <RichTextEditor
                value={form.watch('personalization.informativeAnnouncement') || ''}
                onChange={(value) => form.setValue('personalization.informativeAnnouncement', value)}
                placeholder={t('admin.configuration.general.informativeAnnouncementPlaceholder')}
                rows={5}
                disabled={!isEditing}
                theme={{
                  colors: {
                    border: 'var(--border)',
                    background: 'var(--surface)',
                    backgroundTertiary: 'var(--background-tertiary)',
                    foreground: 'var(--foreground)',
                    foregroundSecondary: 'var(--foreground-secondary)'
                  }
                }}
              />
              <p className="mt-1 text-xs text-foreground-secondary">
                Ce texte s'affichera de manière visible sur votre formulaire de commande.
              </p>
            </div>
          </CardBody>
        </Card>

        {/* Configuration du format du champ modèle produit */}
        <Card variant="default">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-info-10 flex items-center justify-center">
                <Settings className="w-5 h-5 text-info" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('admin.configuration.personalization.productModelFormat')}</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">{t('admin.configuration.personalization.productModelFormatDescription')}</p>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {/* Modèles prédéfinis */}
            <div className="space-y-2">
              <label htmlFor="fieldTemplate" className="block text-sm font-medium text-foreground">
                Modèles par secteur
              </label>
              <select
                id="fieldTemplate"
                onChange={(e) => {
                  const templateKey = e.target.value;
                  if (templateKey && templateKey !== 'custom') {
                    const templates: Record<string, Array<{ key: string; label: string; placeholder: string; type: 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'url' | 'date'; required: boolean }>> = {
                      'pret-a-porter': [
                        { key: 'model', label: 'Modèle', placeholder: 'Ex: Robe été, Pantalon slim...', type: 'text', required: true },
                        { key: 'taille', label: 'Taille', placeholder: 'Ex: S, M, L, XL...', type: 'text', required: true }
                      ],
                      'bonbons': [
                        { key: 'type', label: 'Type', placeholder: 'Ex: Haribo, Malabar, Chupa Chups...', type: 'text', required: true },
                        { key: 'gramme', label: 'Grammes', placeholder: 'Ex: 100, 200, 500', type: 'number', required: true }
                      ],
                      'electronique': [
                        { key: 'modele', label: 'Modèle', placeholder: 'Ex: iPhone 15, MacBook Pro...', type: 'text', required: true },
                        { key: 'capacite', label: 'Capacité', placeholder: 'Ex: 128GB, 256GB, 512GB...', type: 'text', required: false }
                      ],
                      'cosmetique': [
                        { key: 'produit', label: 'Produit', placeholder: 'Ex: Crème hydratante, Rouge à lèvres...', type: 'text', required: true },
                        { key: 'quantite', label: 'Quantité', placeholder: 'Ex: 50ml, 100ml, 30ml...', type: 'text', required: false }
                      ],
                      'livres': [
                        { key: 'titre', label: 'Titre', placeholder: 'Ex: Le Seigneur des Anneaux...', type: 'textarea', required: true },
                        { key: 'format', label: 'Format', placeholder: 'Ex: Broché, Poche, Ebook...', type: 'text', required: false }
                      ],
                      'accessoires': [
                        { key: 'type', label: 'Type', placeholder: 'Ex: Sac, Montre, Bijou...', type: 'text', required: true },
                        { key: 'couleur', label: 'Couleur', placeholder: 'Ex: Noir, Blanc, Rouge...', type: 'text', required: false }
                      ],
                      'alimentaire': [
                        { key: 'produit', label: 'Produit', placeholder: 'Ex: Pizza, Burger, Salade...', type: 'text', required: true },
                        { key: 'portion', label: 'Portion', placeholder: 'Ex: Petite, Moyenne, Grande...', type: 'text', required: false }
                      ],
                      'simple': [
                        { key: 'model', label: 'Modèle', placeholder: 'Ex: Nom du produit, Référence...', type: 'text' as const, required: false }
                      ]
                    };
                    const template = templates[templateKey];
                    if (template) {
                      form.setValue('personalization.productModelConfig.customFields', template);
                      e.target.value = 'custom'; // Reset select
                    }
                  }
                }}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                defaultValue="custom"
              >
                <option value="custom">Personnalisé (actuel)</option>
                <option value="pret-a-porter">👔 Prêt-à-porter (Modèle + Taille)</option>
                <option value="bonbons">🍬 Bonbons (Type + Grammes)</option>
                <option value="electronique">📱 Électronique (Modèle + Capacité)</option>
                <option value="cosmetique">💄 Cosmétique (Produit + Quantité)</option>
                <option value="livres">📚 Livres (Titre + Format)</option>
                <option value="accessoires">👝 Accessoires (Type + Couleur)</option>
                <option value="alimentaire">🍕 Alimentaire (Produit + Portion)</option>
                <option value="simple">📝 Simple (Modèle uniquement)</option>
              </select>
              <p className="text-xs text-foreground-secondary">Sélectionnez un modèle pour préremplir les champs, puis personnalisez-les selon vos besoins</p>
            </div>

            {/* Gestion des champs personnalisés */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="block text-sm font-medium text-foreground">
                  Champs personnalisés
                </div>
                {isEditing && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const currentFields = form.getValues('personalization.productModelConfig.customFields') || [];
                      form.setValue('personalization.productModelConfig.customFields', [
                        ...currentFields,
                        {
                          key: `field_${Date.now()}`,
                          label: '',
                          placeholder: '',
                          type: 'text' as const,
                          required: false,
                        },
                      ]);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un champ
                  </Button>
                )}
              </div>

              {(form.watch('personalization.productModelConfig.customFields') || []).map((field, index) => (
                <div key={field.key || `field-${index}`} className="p-4 border border-border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-foreground">Champ {index + 1}</h4>
                    {isEditing && (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          const currentFields = form.getValues('personalization.productModelConfig.customFields') || [];
                          form.setValue('personalization.productModelConfig.customFields', currentFields.filter((_, i) => i !== index));
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`custom-field-label-${index}`} className="block text-xs font-medium text-foreground-secondary mb-1">
                      Label *
                    </label>
                    <input
                      id={`custom-field-label-${index}`}
                      type="text"
                      {...form.register(`personalization.productModelConfig.customFields.${index}.label` as const, {
                        onChange: (e) => {
                          // Générer automatiquement la clé à partir du label
                          const label = e.target.value;
                          if (label) {
                            const key = label.toLowerCase()
                              .trim()
                              .replace(/[^a-z0-9]+/g, '_')
                              .replace(/^_+|_+$/g, '') || `field_${Date.now()}`;
                            form.setValue(`personalization.productModelConfig.customFields.${index}.key` as any, key);
                          }
                        }
                      })}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                      placeholder="ex: Type, Taille, Couleur"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor={`custom-field-placeholder-${index}`} className="block text-xs font-medium text-foreground-secondary mb-1">
                        Placeholder
                      </label>
                      <input
                        id={`custom-field-placeholder-${index}`}
                        type="text"
                        {...form.register(`personalization.productModelConfig.customFields.${index}.placeholder` as const)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                        placeholder="ex: Saisissez..."
                      />
                    </div>
                    <div>
                      <label htmlFor={`custom-field-type-${index}`} className="block text-xs font-medium text-foreground-secondary mb-1">
                        Type d'input
                      </label>
                      <select
                        id={`custom-field-type-${index}`}
                        {...form.register(`personalization.productModelConfig.customFields.${index}.type` as const)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                      >
                        <option value="text">Texte</option>
                        <option value="textarea">Zone de texte</option>
                        <option value="number">Nombre</option>
                        <option value="email">Email</option>
                        <option value="tel">Téléphone</option>
                        <option value="url">URL</option>
                        <option value="date">Date</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      id={`custom-field-required-${index}`}
                      type="checkbox"
                      {...form.register(`personalization.productModelConfig.customFields.${index}.required` as const)}
                      disabled={!isEditing}
                      className="mr-2"
                    />
                    <label htmlFor={`custom-field-required-${index}`} className="text-xs text-foreground-secondary">Champ requis</label>
                  </div>
                </div>
              ))}

              {(!form.watch('personalization.productModelConfig.customFields') || form.watch('personalization.productModelConfig.customFields')?.length === 0) && (
                <div className="p-4 text-center text-sm text-foreground-secondary border border-border rounded-lg">
                  Aucun champ personnalisé. Cliquez sur "Ajouter un champ" pour en créer un.
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Boutons d'action en bas du formulaire */}
        {isEditing && (
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-border">
            <Button
              variant="ghost"
              size="md"
              onClick={handleCancel}
              className="w-full sm:w-auto"
            >
              {t('admin.configuration.general.cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={isSaving}
              isLoading={isSaving}
              className="w-full sm:w-auto"
            >
              {t('admin.configuration.general.save')}
            </Button>
          </div>
        )}
      </form>
    </div >
  );
};

export default PersonalizationConfiguration;

